import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  FormControl, InputLabel, MenuItem, Select, Stack, Switch, TextField, Typography
} from '@mui/material';
import {
  AccessTime, CardGiftcard, Favorite, Groups, LocalOffer, NotificationsActive,
  RateReview, Replay, Search, Share, ShoppingBasket, Sort, SupportAgent,
  Timeline
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../services/AuthService';

const FeatureSwitch = ({ icon, title, description, checked, onChange, children, available = false }) => {
  const isAvailable = available || [
    'Repetir pedido',
    'Avaliações verificadas',
    'Programa de fidelidade',
    'Benefício de primeira compra',
    'Rastreamento visual'
  ].includes(title);
  return (
  <Card variant="outlined" sx={{ borderRadius: 3, borderColor: checked ? '#ffb59a' : '#e2e8f0', bgcolor: checked ? '#fffaf7' : '#fff' }}>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: checked ? '#E50914' : '#f1f5f9', color: checked ? '#fff' : '#64748b', flexShrink: 0 }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography fontWeight={900}>{title}</Typography>
              {!isAvailable && <Chip label="Em breve" size="small" variant="outlined" />}
            </Stack>
            <Switch disabled={!isAvailable} checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{description}</Typography>
          {checked && children && <Box sx={{ mt: 2 }}>{children}</Box>}
        </Box>
      </Stack>
    </CardContent>
  </Card>
  );
};

export default function CustomerFeatures() {
  const { api, user, isAdminMaster } = useAuth();
  const [features, setFeatures] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const endpoint = isAdminMaster() && !user?.lanchonete_id
      ? null
      : `/customer-features/${user?.lanchonete_id || ''}`;
    if (!endpoint) return;
    const { data } = await api.get(endpoint);
    setFeatures(data.features);
  }, [api, isAdminMaster, user?.lanchonete_id]);

  useEffect(() => {
    load().catch((error) => toast.error(error.response?.data?.error || 'Erro ao carregar recursos'));
  }, [load]);

  const set = (key, value) => setFeatures((current) => ({ ...current, [key]: value }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/customer-features/${user?.lanchonete_id || ''}`, features);
      setFeatures(data.features);
      toast.success('Recursos para clientes atualizados!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar recursos');
    } finally {
      setSaving(false);
    }
  };

  if (!features) return <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 3 }}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h4" fontWeight={900}>Recursos para clientes</Typography>
            <Chip label="Você escolhe" color="primary" size="small" />
          </Stack>
          <Typography color="text.secondary" sx={{ mt: 1 }}>Ligue somente as vantagens que fazem sentido para sua operação.</Typography>
        </Box>
        <Button variant="contained" size="large" onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar alterações'}</Button>
      </Stack>
      <Alert severity="info" sx={{ mb: 3 }}>
        Alterações desligadas não aparecem para o cliente. Valores promocionais são custeados pelo próprio estabelecimento.
      </Alert>

      <Typography variant="h6" fontWeight={900} sx={{ mb: 1.5 }}>Compra e fidelização</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { md: 'repeat(2, 1fr)' }, gap: 2, mb: 4 }}>
        <FeatureSwitch icon={<Replay />} title="Repetir pedido" description="Permite colocar novamente no carrinho os itens de um pedido anterior." checked={features.reorder_enabled} onChange={(value) => set('reorder_enabled', value)} />
        <FeatureSwitch icon={<RateReview />} title="Avaliações verificadas" description="Clientes com pedidos concluídos poderão avaliar a loja." checked={features.reviews_enabled} onChange={(value) => set('reviews_enabled', value)} />
        <FeatureSwitch icon={<Favorite />} title="Programa de fidelidade" description="O cliente recebe uma recompensa após completar a quantidade de pedidos definida." checked={features.loyalty_enabled} onChange={(value) => set('loyalty_enabled', value)}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField label="Pedidos necessários" type="number" value={features.loyalty_orders_required} onChange={(e) => set('loyalty_orders_required', e.target.value)} inputProps={{ min: 2, max: 50 }} />
            <TextField label="Recompensa (R$)" type="number" value={features.loyalty_reward_amount} onChange={(e) => set('loyalty_reward_amount', e.target.value)} inputProps={{ min: 1, step: .5 }} />
          </Stack>
        </FeatureSwitch>
        <FeatureSwitch icon={<LocalOffer />} title="Benefício de primeira compra" description="Aplica automaticamente o desconto no primeiro pedido do cliente nesta loja." checked={features.first_order_coupon_enabled} onChange={(value) => set('first_order_coupon_enabled', value)}>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <FormControl fullWidth><InputLabel>Tipo</InputLabel><Select label="Tipo" value={features.first_order_discount_type} onChange={(e) => set('first_order_discount_type', e.target.value)}><MenuItem value="fixed">Valor em reais</MenuItem><MenuItem value="percentage">Porcentagem</MenuItem></Select></FormControl>
              <TextField fullWidth label={features.first_order_discount_type === 'percentage' ? 'Desconto (%)' : 'Desconto (R$)'} type="number" value={features.first_order_discount_value} onChange={(e) => set('first_order_discount_value', e.target.value)} />
            </Stack>
            <TextField label="Pedido mínimo (R$)" type="number" value={features.first_order_minimum} onChange={(e) => set('first_order_minimum', e.target.value)} />
          </Stack>
        </FeatureSwitch>
        <FeatureSwitch icon={<Share />} title="Indique um amigo" description="Quem indica e o novo cliente recebem benefício após o primeiro pedido pago." checked={features.referral_enabled} onChange={(value) => set('referral_enabled', value)}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField label="Quem indicou (R$)" type="number" value={features.referral_inviter_reward} onChange={(e) => set('referral_inviter_reward', e.target.value)} />
            <TextField label="Novo cliente (R$)" type="number" value={features.referral_friend_reward} onChange={(e) => set('referral_friend_reward', e.target.value)} />
          </Stack>
        </FeatureSwitch>
        <FeatureSwitch icon={<CardGiftcard />} title="Preços exclusivos no app" description="Permite destacar vantagens disponíveis apenas para pedidos feitos pelo All Delivery." checked={features.exclusive_prices_enabled} onChange={(value) => set('exclusive_prices_enabled', value)} />
      </Box>

      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" fontWeight={900} sx={{ mb: 1.5 }}>Descoberta e pedido</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { md: 'repeat(2, 1fr)' }, gap: 2, mb: 4 }}>
        <FeatureSwitch icon={<Sort />} title="Ordenação avançada" description="Permite ordenar lojas por distância, tempo, taxa, avaliação e pedido mínimo." checked={features.sorting_enabled} onChange={(value) => set('sorting_enabled', value)} />
        <FeatureSwitch icon={<Search />} title="Busca por produto" description="O cliente poderá procurar hambúrguer, pizza, açaí e outros produtos." checked={features.product_search_enabled} onChange={(value) => set('product_search_enabled', value)} />
        <FeatureSwitch icon={<Groups />} title="Mais pedidos perto de você" description="Exibe produtos populares da região do cliente." checked={features.popular_nearby_enabled} onChange={(value) => set('popular_nearby_enabled', value)} />
        <FeatureSwitch icon={<ShoppingBasket />} title="Combos e compre junto" description="Habilita sugestões de itens complementares no produto e no carrinho." checked={features.combos_enabled} onChange={(value) => set('combos_enabled', value)} />
        <FeatureSwitch icon={<AccessTime />} title="Agendamento de pedido" description="O cliente poderá escolher uma data e horário futuro para receber ou retirar." checked={features.scheduling_enabled} onChange={(value) => set('scheduling_enabled', value)}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField label="Antecedência mínima (min)" type="number" value={features.scheduling_min_minutes} onChange={(e) => set('scheduling_min_minutes', e.target.value)} />
            <TextField label="Máximo de dias" type="number" value={features.scheduling_max_days} onChange={(e) => set('scheduling_max_days', e.target.value)} />
          </Stack>
        </FeatureSwitch>
        <FeatureSwitch icon={<CardGiftcard />} title="Endereços favoritos" description="Permite salvar casa, trabalho e outros endereços para pedidos futuros." checked={features.saved_addresses_enabled} onChange={(value) => set('saved_addresses_enabled', value)} />
      </Box>

      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" fontWeight={900} sx={{ mb: 1.5 }}>Acompanhamento e relacionamento</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { md: 'repeat(2, 1fr)' }, gap: 2, mb: 4 }}>
        <FeatureSwitch icon={<Timeline />} title="Rastreamento visual" description="Mostra recebido, aceito, preparando, saiu para entrega e entregue." checked={features.tracking_enabled} onChange={(value) => set('tracking_enabled', value)} />
        <FeatureSwitch icon={<NotificationsActive />} title="Recuperação de carrinho" description="Lembra o cliente quando ele deixa produtos no carrinho sem finalizar." checked={features.cart_recovery_enabled} onChange={(value) => set('cart_recovery_enabled', value)}>
          <TextField label="Enviar lembrete após (min)" type="number" value={features.cart_recovery_minutes} onChange={(e) => set('cart_recovery_minutes', e.target.value)} />
        </FeatureSwitch>
        <FeatureSwitch icon={<SupportAgent />} title="Suporte e solicitação de reembolso" description="Exibe caminhos claros para ajuda e solicitação de análise do pedido." checked={features.support_refunds_enabled} onChange={(value) => set('support_refunds_enabled', value)} />
      </Box>

      <Stack direction="row" justifyContent="flex-end"><Button variant="contained" size="large" onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar alterações'}</Button></Stack>
    </Box>
  );
}
